import { GroupRoles } from '@bp/pim-auth-constants';
import { ModalBottomButtons } from '@bp/ui-components';
import { FragmentType, getFragmentData, graphql } from 'client/gql';
import { ProfileSelection } from 'components/ProfileSelection/ProfileSelection';
import { Form, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'urql';
import { showSuccessUpdateToast, showUserDbErrorToast } from 'utils/toasts';
import { uniqueByProfileUuid } from 'utils/uniquByUuid';
import { CourseMember } from '../types/types';
import { UpdateCoursesMutationDocument } from './CourseForm';

export const CourseEditorsFormFragment = graphql(`
  fragment CourseEditorsFormFields on Query {
    course: groups(where: { uuid: $courseUuid }) {
      __typename
      uuid
      editors {
        __typename
        uuid
        displayName
        organizationRoles
      }
      viewers {
        __typename
        uuid
      }
    }
    ...ProfileSelectionFields
  }
`);

type CourseEditorsFormType = {
  editors: CourseMember[];
};

type CourseEditorsFormProps = {
  courseUuid: string;
  schoolYearUuid: string;
  onLoad: (isLoading: boolean) => void;
  onClose: () => void;
  query: FragmentType<typeof CourseEditorsFormFragment> | undefined;
};

export const CourseEditorsForm = ({ courseUuid, schoolYearUuid, onLoad, onClose, query }: CourseEditorsFormProps) => {
  const { t } = useTranslation();

  const fragment = getFragmentData(CourseEditorsFormFragment, query);

  const course = fragment?.course[0];

  const [, updateCourse] = useMutation(UpdateCoursesMutationDocument);

  const handleSubmit = async (values: CourseEditorsFormType, helpers: FormikHelpers<CourseEditorsFormType>) => {
    onLoad(true);

    const { editors } = values;

    const editorUuids = uniqueByProfileUuid(editors).map((e) => e.profileUuid);

    const { error } = await updateCourse({
      update: { editors: editorUuids },
      uuid: courseUuid,
    });
    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessUpdateToast();
    }

    onLoad(false);
    helpers.resetForm();
    onClose();
  };

  const initialValues: CourseEditorsFormType = {
    editors:
      course?.editors.map((e) => ({
        ...e,
        profileUuid: e.uuid,
        name: e.displayName ?? '',
        groupRole: GroupRoles.Editor,
      })) ?? [],
  };

  return (
    <Formik<CourseEditorsFormType> onSubmit={handleSubmit} initialValues={initialValues}>
      {({ setFieldValue, values }) => (
        <Form>
          <ProfileSelection
            initialMembers={values.editors.map((m) => ({
              ...m,
              uuid: m.profileUuid,
              organizationRoles: m.organizationRoles,
              selected: false,
            }))}
            excludeMemberUuids={course?.viewers.map((v) => v.uuid)}
            onPick={async (picked) => {
              const editors: CourseMember[] = picked.map((p) => ({
                ...p,
                profileUuid: p.uuid,
                groupRole: GroupRoles.Editor,
              }));
              await setFieldValue('editors', editors);
            }}
            schoolYearUuid={schoolYearUuid}
            query={fragment}
          />

          <ModalBottomButtons
            closeButton={{
              callback: onClose,
            }}
            additionalLeftComponent={t('courses.editorsHint')}
          />
        </Form>
      )}
    </Formik>
  );
};
